import {
  Button,
  Field,
  FieldError,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Textarea,
} from "@/components/ui";
import { TRACKERS } from "@/constants/Trackers";
import { trackEvent } from "@/lib/tracking";
import { zodResolver } from "@hookform/resolvers/zod";
import type * as fhirTypes from "fhir/r4";
import { Controller, useForm } from "react-hook-form";
import type { z } from "zod";
import { useDispositionCodes } from "../network/useDispositionCodes";
import { editDispositionSchema } from "./dispositionSchemas";

interface DispositionEditorProps {
  onSubmit(v: z.infer<typeof editDispositionSchema>): void;
  onCancel(): void;
  isLoading: boolean;
  task?: fhirTypes.Task;
}

export const DispositionEditor = ({
  task,
  onCancel,
  onSubmit,
  isLoading,
}: DispositionEditorProps) => {
  const { data: dispositionCodesData } = useDispositionCodes();

  const form = useForm<z.infer<typeof editDispositionSchema>>({
    resolver: zodResolver(editDispositionSchema),
    defaultValues: {
      code: task?.code?.coding?.[0].code || "",
      text: task?.description || "",
    },
  });

  const handleCodeChange = (value: string) => {
    form.setValue("code", value);

    form.setValue(
      "text",
      dispositionCodesData?.dispositionCodes.find((code) => code.code === value)
        ?.defaultValue || "",
    );
  };

  return (
    <form
      onSubmit={form.handleSubmit(onSubmit)}
      className="flex flex-col gap-2"
      data-testid="edit-disposition-form"
    >
      <div className="flex flex-row gap-2">
        <Controller
          control={form.control}
          name="code"
          render={({ field, fieldState }) => (
            <Field data-invalid={fieldState.invalid}>
              <Select
                value={field.value}
                onValueChange={handleCodeChange}
                disabled={field.disabled}
              >
                <SelectTrigger
                  data-testid="disposition-select"
                  className="w-[175px]"
                  aria-invalid={fieldState.invalid}
                >
                  <SelectValue placeholder="Select" />
                </SelectTrigger>
                <SelectContent>
                  {dispositionCodesData?.dispositionCodes.map(
                    ({ code, label }) => (
                      <SelectItem key={code} value={code}>
                        {label}
                      </SelectItem>
                    ),
                  )}
                </SelectContent>
              </Select>
              <FieldError errors={[fieldState.error]} />
            </Field>
          )}
        />

        <Controller
          control={form.control}
          name="text"
          render={({ field, fieldState }) => (
            <Field className="w-full" data-invalid={fieldState.invalid}>
              <Textarea
                {...field}
                data-testid="disposition-text"
                autoFocus
                id="text"
                aria-invalid={fieldState.invalid}
              />
              <FieldError errors={[fieldState.error]} />
            </Field>
          )}
        />
      </div>

      <div className="flex gap-2 justify-end">
        <Button
          size="xs"
          variant="secondary"
          onClick={() => {
            trackEvent(TRACKERS.CANCEL_DISPOSITION);
            onCancel();
          }}
        >
          Cancel
        </Button>
        <Button
          size="xs"
          type="submit"
          isLoading={isLoading}
          onClick={() => trackEvent(TRACKERS.SAVE_DISPOSITION)}
        >
          Save
        </Button>
      </div>
    </form>
  );
};
